import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';
import { TwoFactorAuthService } from 'src/app/services/two-factor/two-factor-auth.service';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { PendingTwoFactorAuth } from 'src/app/models/two-factor.model';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-verify-2fa',
  templateUrl: './verify-2fa.component.html',
  styleUrls: ['./verify-2fa.component.css']
})
export class Verify2faComponent implements OnInit {
  verifyForm: FormGroup;
  pendingAuth: PendingTwoFactorAuth | null = null;
  isLoading: boolean = false;
  showBackupCodeForm: boolean = false;
  attemptsLeft: number = 3; // Límite de intentos
  codeDigits: string[] = ['', '', '', '', '', ''];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private twoFactorService: TwoFactorAuthService,
    private afs: AngularFirestore
  ) {
    this.verifyForm = this.fb.group({
      code: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]]
    });
  }

  ngOnInit(): void {
    // Recuperar información de autenticación pendiente
    const pendingAuthData = sessionStorage.getItem('pending2FAAuth');
    if (!pendingAuthData) {
      this.router.navigateByUrl('/login');
      return;
    }
    
    this.pendingAuth = JSON.parse(pendingAuthData);
    // Si falta initialToken, intentar recuperarlo y persistirlo
    if (this.pendingAuth && !(this.pendingAuth as any).initialToken) {
      this.authService.getJWTToken().then((token) => {
        if (token && this.pendingAuth) {
          const updated = { ...this.pendingAuth, initialToken: token } as any;
          sessionStorage.setItem('pending2FAAuth', JSON.stringify(updated));
          this.pendingAuth = updated;
          console.log('[DEBUG] verify-2fa: initialToken recuperado y persistido');
        }
      });
    }
  }

  // Función para manejar input en las casillas individuales
  onDigitInput(event: any, index: number) {
    const value = event.target.value;
    
    // Solo permitir números
    if (!/^\d?$/.test(value)) {
      event.target.value = '';
      return;
    }

    this.codeDigits[index] = value;
    
    // Actualizar el valor del formulario
    const fullCode = this.codeDigits.join('');
    this.verifyForm.patchValue({ code: fullCode });

    // Mover al siguiente input si hay un valor
    if (value && index < 5) {
      const nextInput = document.querySelectorAll('.digit-input')[index + 1] as HTMLInputElement;
      if (nextInput) {
        nextInput.focus();
      }
    }
  }

  // Función para manejar teclas especiales
  onKeyDown(event: KeyboardEvent, index: number) {
    // Backspace: borrar y mover al anterior
    if (event.key === 'Backspace') {
      if (!this.codeDigits[index] && index > 0) {
        const prevInput = document.querySelectorAll('.digit-input')[index - 1] as HTMLInputElement;
        if (prevInput) {
          this.codeDigits[index - 1] = '';
          prevInput.value = '';
          prevInput.focus();
          
          // Actualizar formulario
          const fullCode = this.codeDigits.join('');
          this.verifyForm.patchValue({ code: fullCode });
        }
      } else {
        this.codeDigits[index] = '';
        // Actualizar formulario
        const fullCode = this.codeDigits.join('');
        this.verifyForm.patchValue({ code: fullCode });
      }
    }
    
    // Flecha izquierda: mover al anterior
    if (event.key === 'ArrowLeft' && index > 0) {
      const prevInput = document.querySelectorAll('.digit-input')[index - 1] as HTMLInputElement;
      if (prevInput) {
        prevInput.focus();
      }
    }
    
    // Flecha derecha: mover al siguiente
    if (event.key === 'ArrowRight' && index < 5) {
      const nextInput = document.querySelectorAll('.digit-input')[index + 1] as HTMLInputElement;
      if (nextInput) {
        nextInput.focus();
      }
    }

    // Enter: enviar formulario si está completo
    if (event.key === 'Enter' && this.verifyForm.valid) {
      this.onSubmit();
    }
  }

  async onSubmit() {
    if (this.verifyForm.invalid || !this.pendingAuth) return;
    
    this.isLoading = true;
    
    try {
      // Utilizamos la API en lugar del backend para validar y setear Custom Claims Firebase
      const isValid = await this.twoFactorService.verifyTokenAPI(this.verifyForm.value.code);
      
      if (isValid) {
        // Refrescar el token JWT local obligatoriamente para inyectar los nuevos Claims en el navegador
        try {
          const currentUser = await this.authService.userAuth.getValue();
          if (currentUser) {
            await currentUser.getIdToken(true);
          } else {
            await this.authService.getJWTToken();
          }
        } catch {
          await this.authService.getJWTToken();
        }

        // Actualizar última vez usado en la colección two-factor-auth
        await this.afs.collection('two-factor-auth').doc(this.pendingAuth.uid).update({
          lastUsed: new Date()
        });
        
        // Completar el login
        this.completeLogin();
      } else {
        this.attemptsLeft--;
        
        if (this.attemptsLeft <= 0) {
          // Si se agotaron los intentos, forzar cierre de sesión
          this.cancel();
          Swal.fire('Error', 'Demasiados intentos fallidos. Por favor, intenta iniciar sesión de nuevo.', 'error');
        } else {
          Swal.fire('Error', `Código incorrecto. Te quedan ${this.attemptsLeft} intentos.`, 'error');
        }
      }
    } catch (error) {
      console.error('Error al verificar 2FA:', error);
      Swal.fire('Error', 'Ha ocurrido un error durante la verificación', 'error');
    } finally {
      this.isLoading = false;
    }
  }

  async useBackupCode() {
    this.showBackupCodeForm = true;
  }

  async verifyBackupCode(backupCode: string) {
    if (!backupCode || !this.pendingAuth) return;
    
    this.isLoading = true;
    
    try {
      const isValid = await this.twoFactorService.verifyAndConsumeBackupCode(
        this.pendingAuth.uid,
        backupCode.trim()
      );
      
      if (isValid) {
        // Completar el login
        this.completeLogin();
      } else {
        Swal.fire('Error', 'Código de respaldo inválido.', 'error');
      }
    } catch (error) {
      console.error('Error al verificar código de respaldo:', error);
      Swal.fire('Error', 'Ha ocurrido un error durante la verificación', 'error');
    } finally {
      this.isLoading = false;
    }
  }

  private completeLogin() {
    if (!this.pendingAuth) return;
    
    // Limpiar datos temporales
    sessionStorage.removeItem('pending2FAAuth');
    
    // Completar autenticación y redirigir
    this.authService.completeLoginAfter2FA(this.pendingAuth.uid);
  }

  cancel() {
    sessionStorage.removeItem('pending2FAAuth');
    this.authService.logout();
    this.router.navigateByUrl('/login');
  }
}
